// ============================================
// CONTAINER QUOTE WIZARD - RATE ENGINE
// Asks the right questions in the right order,
// calls /api/rate-cards/calculate, and passes the
// deterministic result up to CreateQuote.
// ============================================
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  FaBoxes, FaTruckLoading, FaRoute, FaClock,
  FaCalculator, FaCheckCircle, FaSpinner, FaExclamationTriangle,
  FaInfoCircle, FaMapMarkerAlt, FaTruck,
  FaSyncAlt, FaArrowRight
} from 'react-icons/fa';
import { rateCards } from '../api';
import InteractiveRouteMap from './InteractiveRouteMap';
import { useGoogleMaps } from '../hooks/useGoogleMaps';

// ============================================
// STEP LIST
// ============================================

const STEPS = [
  { id: 'freight', label: 'Freight', icon: FaBoxes },
  { id: 'site', label: 'Site & Crane', icon: FaTruck },
  { id: 'route', label: 'Route', icon: FaRoute },
  { id: 'extras', label: 'Extras', icon: FaClock }
];

const EMPTY_WIZARD = {
  container_size: '6m',
  container_type: 'dry',
  is_empty: true,
  container_count: 1,
  combined_weight_t: '',
  mixed_sizes: false,
  site_loading: false,
  ground_condition: 'flat',
  pickup_town: '',
  dropoff_town: '',
  cross_border: false,
  weekend: false,
  extra_hours: '',
  standing_hours: '',
  extra_stops: '',
  mine_site: false,
  repo_move: false,
  addons: [],
  multi_leg_mode: 'one_trip',
  vat_included: false
};

// ============================================
// COMPONENT
// ============================================
function ContainerQuoteWizard({ initialData, onCalculated, onClear, onStateChange }) {
  const { isLoaded } = useGoogleMaps();
  const [step, setStep] = useState(0);
  const [wizard, setWizard] = useState({ ...EMPTY_WIZARD, ...(initialData || {}) });
  const [pickupTownOptions, setPickupTownOptions] = useState([]);
  const [dropoffTownOptions, setDropoffTownOptions] = useState([]);
  const [addons, setAddons] = useState([]);
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [townSearching, setTownSearching] = useState(false);
  const [mapDistance, setMapDistance] = useState(null);

  // ============================================
  // LOAD MASTER DATA
  // ============================================
  useEffect(() => {
    rateCards.getAddons().then(res => {
      if (res.success) setAddons(res.addons || []);
    }).catch(() => { /* non-critical */ });
  }, []);

  // ============================================
  // LIVE STATE REPORTING (AI memory)
  // The page listens to this so every /assist call
  // knows exactly what is already filled in.
  // ============================================
  useEffect(() => {
    if (onStateChange) onStateChange(wizard);
  }, [wizard, onStateChange]);

  // ============================================
  // TOWN SEARCH (debounced)
  // ============================================
  const searchTowns = useCallback(async (query, setOptions) => {
    if (!query || query.trim().length < 2) {
      setOptions([]);
      return;
    }
    setTownSearching(true);
    try {
      const res = await rateCards.getTowns({ search: query.trim(), limit: 12 });
      setOptions((res.success && res.towns) ? res.towns : []);
    } catch {
      setOptions([]);
    } finally {
      setTownSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (step !== 2) return;
      const q = wizard.pickup_town || '';
      if (q.trim().length >= 2) searchTowns(q, setPickupTownOptions);
    }, 350);
    return () => clearTimeout(timer);
  }, [step, wizard.pickup_town, searchTowns]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (step !== 2) return;
      const q = wizard.dropoff_town || '';
      if (q.trim().length >= 2) searchTowns(q, setDropoffTownOptions);
    }, 350);
    return () => clearTimeout(timer);
  }, [step, wizard.dropoff_town, searchTowns]);

  // Listen for AI chat autofill event (dispatched by ContainerQuotePage)
  useEffect(() => {
    const handleFill = (e) => {
      const fields = e.detail || {};
      if (Object.keys(fields).length === 0) return;
      setWizard(prev => {
        const merged = { ...prev };
        Object.keys(fields).forEach(k => {
          if (fields[k] !== null && fields[k] !== undefined && fields[k] !== '') {
            merged[k] = fields[k];
          }
        });
        return merged;
      });
      setStep(0);
    };

    window.addEventListener('container-wizard-fill', handleFill);
    return () => window.removeEventListener('container-wizard-fill', handleFill);
  }, []);

  // ============================================
  // FIELD SETTERS
  // ============================================
  const set = (key, value) => setWizard(prev => ({ ...prev, [key]: value }));

  const toggleAddon = (id) => {
    setWizard(prev => {
      const has = (prev.addons || []).includes(id);
      return {
        ...prev,
        addons: has ? prev.addons.filter(a => a !== id) : [...(prev.addons || []), id]
      };
    });
  };

  // ============================================
  // VALIDATION PER STEP
  // ============================================
  const validateStep = (idx) => {
    if (idx === 0) {
      const count = parseInt(wizard.container_count) || 0;
      if (count < 1) { toast.error('Select at least 1 container'); return false; }
      if (count > 3) { toast.error('Maximum 3 containers on a single quote. Use separate jobs for more.'); return false; }
      return true;
    }
    if (idx === 2) {
      if (!wizard.dropoff_town || wizard.dropoff_town.trim().length < 2) {
        toast.error('Enter a drop-off town');
        return false;
      }
      return true;
    }
    return true;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep(s => Math.max(s - 1, 0));

  // ============================================
  // CALCULATE
  // ============================================
  const handleCalculate = async () => {
    for (let i = 0; i <= 2; i++) {
      if (!validateStep(i)) { setStep(i); return; }
    }

    setCalculating(true);
    try {
      const payload = { ...wizard };
      if (payload.combined_weight_t === '') payload.combined_weight_t = 0;
      if (payload.extra_hours === '') payload.extra_hours = 0;
      if (payload.standing_hours === '') payload.standing_hours = 0;
      if (payload.extra_stops === '') payload.extra_stops = 0;

      const res = await rateCards.calculate(payload);
      if (res.success) {
        setResult(res.quote);
        if (onCalculated) onCalculated(res.quote);
        toast.success('Quote calculated');
      } else {
        toast.error(res.error || 'Calculation failed');
      }
    } catch (err) {
      toast.error(err.message || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const handleClear = () => {
    setWizard({ ...EMPTY_WIZARD });
    setResult(null);
    if (onClear) onClear();
  };

  // ============================================
  // SHARED UI
  // ============================================
  const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";

  const TownField = ({ label, value, onChange, options = [], placeholder }) => (
    <div className="relative z-10">
      <label className={labelCls}>{label}</label>
      <div className="relative">
        <FaMapMarkerAlt className="absolute left-3 top-3 text-gray-400" />
        <input
          className={inputCls + " pl-9"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || 'Type a town…'}
          autoComplete="off"
        />
        {townSearching && <FaSpinner className="absolute right-3 top-3 animate-spin text-yellow-500" />}
      </div>
      {options.length > 0 && (
        <div className="mt-1 border border-gray-200 rounded-lg max-h-40 overflow-y-auto bg-white shadow-lg z-20">
          {options.map((t) => (
            <button
              type="button"
              key={t.id}
              className="w-full text-left px-3 py-2 hover:bg-yellow-50 text-sm flex justify-between items-center"
              onClick={() => { onChange(t.town_name); setPickupTownOptions([]); setDropoffTownOptions([]); }}
            >
              <span>{t.town_name}</span>
              <span className="text-xs text-gray-500">{Math.round(t.km_from_edenvale)}km · {t.province || ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const ToggleBtn = ({ active, onClick, children }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-lg border text-sm font-bold transition-colors shadow ${
        active ? 'bg-yellow-400 text-navy border-yellow-400' : 'bg-white text-gray-700 border-gray-300 hover:border-yellow-400'
      }`}
    >
      {children}
    </button>
  );

  // ============================================
  // STEPS
  // ============================================
  const renderFreight = () => (
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Container Size</label>
        <div className="flex gap-2">
          <ToggleBtn active={wizard.container_size === '6m'} onClick={() => set('container_size', '6m')}>6m (20ft)</ToggleBtn>
          <ToggleBtn active={wizard.container_size === '12m'} onClick={() => set('container_size', '12m')}>12m (40ft)</ToggleBtn>
        </div>
      </div>

      <div>
        <label className={labelCls}>Container Type</label>
        <div className="flex flex-wrap gap-2">
          {['dry', 'reefer', 'open_top', 'flat_rack'].map(t => (
            <ToggleBtn key={t} active={wizard.container_type === t} onClick={() => set('container_type', t)}>
              {t.replace('_', ' ')}
            </ToggleBtn>
          ))}
        </div>
      </div>

      <div>
        <label className={labelCls}>Empty or Loaded?</label>
        <div className="flex gap-2">
          <ToggleBtn active={wizard.is_empty === true} onClick={() => set('is_empty', true)}>Empty</ToggleBtn>
          <ToggleBtn active={wizard.is_empty === false} onClick={() => set('is_empty', false)}>Loaded</ToggleBtn>
        </div>
        {wizard.is_empty === false && (
          <p className="mt-2 text-xs text-amber-700 flex items-center gap-1">
            <FaExclamationTriangle /> Loaded containers are heavier — crane/weight requirements must be confirmed on site.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Number of Containers</label>
          <input
            type="number"
            min="1"
            max="3"
            className={inputCls}
            value={wizard.container_count}
            onChange={(e) => set('container_count', parseInt(e.target.value) || 1)}
          />
          <p className="text-xs text-gray-500 mt-1">1–3 per quote. More? Quote as separate jobs.</p>
        </div>
        <div>
          <label className={labelCls}>Combined Weight (tonnes)</label>
          <input
            type="number"
            step="0.5"
            className={inputCls}
            value={wizard.combined_weight_t}
            onChange={(e) => set('combined_weight_t', e.target.value)}
            placeholder="e.g. 26 (empties ~0)"
          />
          {parseInt(wizard.combined_weight_t) > 27 && (
            <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
              <FaExclamationTriangle /> Over 27t — side-lifter not legal, will use crane/superlink.
            </p>
          )}
        </div>
      </div>

      {wizard.container_count === 2 && wizard.container_size === '6m' && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={wizard.mixed_sizes}
            onChange={(e) => set('mixed_sizes', e.target.checked)}
            className="rounded"
          />
          Mixed sizes (e.g. one 6m + one 12m) → super-link config
        </label>
      )}
    </div>
  );

  const renderSite = () => (
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Who loads / offloads at the site?</label>
        <div className="flex gap-2">
          <ToggleBtn active={wizard.site_loading === true} onClick={() => set('site_loading', true)}>
            Client loads (no crane needed)
          </ToggleBtn>
          <ToggleBtn active={wizard.site_loading === false} onClick={() => set('site_loading', false)}>
            We must lift (crane / side-lifter)
          </ToggleBtn>
        </div>
      </div>

      <div>
        <label className={labelCls}>Ground Condition (side-lifter only)</label>
        <div className="flex gap-2">
          <ToggleBtn active={wizard.ground_condition === 'flat'} onClick={() => set('ground_condition', 'flat')}>
            Flat / hard (side-lifter OK)
          </ToggleBtn>
          <ToggleBtn active={wizard.ground_condition === 'uneven'} onClick={() => set('ground_condition', 'uneven')}>
            Uneven / soft (needs crane truck)
          </ToggleBtn>
        </div>
        {wizard.ground_condition === 'uneven' && wizard.site_loading === false && wizard.container_count === 2 && wizard.container_size === '6m' && (
          <p className="mt-2 text-xs text-amber-700 flex items-center gap-1">
            <FaExclamationTriangle /> Uneven ground + 2x 6m → will use a super-link crane truck instead of a side-lifter.
          </p>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 space-y-1">
        <p className="font-medium flex items-center gap-1"><FaInfoCircle /> Truck selection</p>
        <p>• 2x 6m empty under 27t → side-lifter (self-loads)</p>
        <p>• 2x 6m over 27t → super-link (crane or no-crane)</p>
        <p>• 12m + 6m / 3x 6m → super-link</p>
        <p>• Single 12m → 12m crane or no-crane tri-axle</p>
        <p>• Client loads → no-crane config; we lift → crane config</p>
      </div>
    </div>
  );

  const renderRoute = () => (
    <div className="space-y-4">
      {isLoaded ? (
        <InteractiveRouteMap
          originValue={wizard.pickup_town}
          destinationValue={wizard.dropoff_town}
          onOriginChange={(v) => set('pickup_town', v)}
          onDestinationChange={(v) => set('dropoff_town', v)}
          onDistanceCalculated={(d) => setMapDistance(d)}
          onRouteChange={(r) => set('route_optimized', r)}
        />
      ) : (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <FaSpinner className="animate-spin" /> Loading map…
        </div>
      )}
      {mapDistance && (
        <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
          <FaRoute className="text-yellow-500" /> Map route: {mapDistance.distance_text} · approx {mapDistance.duration_text}
        </p>
      )}

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={wizard.cross_border}
          onChange={(e) => set('cross_border', e.target.checked)}
          className="rounded"
        />
        Cross-Border move (adds border/customs fees)
      </label>
    </div>
  );

  const renderExtras = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Extra Working Hours</label>
          <input type="number" min="0" step="0.5" className={inputCls} value={wizard.extra_hours}
            onChange={(e) => set('extra_hours', e.target.value)} placeholder="0" />
        </div>
        <div>
          <label className={labelCls}>Standing / Waiting Hours</label>
          <input type="number" min="0" step="0.5" className={inputCls} value={wizard.standing_hours}
            onChange={(e) => set('standing_hours', e.target.value)} placeholder="0" />
        </div>
        <div>
          <label className={labelCls}>Extra Stops</label>
          <input type="number" min="0" className={inputCls} value={wizard.extra_stops}
            onChange={(e) => set('extra_stops', e.target.value)} placeholder="0" />
        </div>
        <div className="flex items-end pb-1">
          <ToggleBtn active={wizard.weekend === true} onClick={() => set('weekend', !wizard.weekend)}>
            Weekend / Overtime
          </ToggleBtn>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={wizard.repo_move}
          onChange={(e) => set('repo_move', e.target.checked)}
          className="rounded"
        />
        Move extra containers on site (repo)
      </label>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={wizard.mine_site}
          onChange={(e) => set('mine_site', e.target.checked)}
          className="rounded"
        />
        Mines / Power Stations (adds safety surcharge)
      </label>

      {addons.length > 0 && (
        <div>
          <label className={labelCls}>Add-ons (permits, border, escort, crane-on-site)</label>
          <div className="space-y-2">
            {addons.map(a => (
              <label key={a.id} className="flex items-center gap-2 text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={(wizard.addons || []).includes(a.id)}
                  onChange={() => toggleAddon(a.id)}
                  className="rounded"
                />
                <span className="flex-1">{a.name}</span>
                <span className="text-xs text-gray-500">
                  {a.default_amount > 0 ? `R${Number(a.default_amount).toLocaleString()}` : ''}
                  {a.is_to_be_confirmed && a.default_amount > 0 ? ' (TBC)' : ''}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ============================================
  // RESULT
  // ============================================
  const renderResult = () => {
    if (!result) return null;

    const fmt = (n) => `R${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <h4 className="font-semibold text-yellow-800 flex items-center gap-2 mb-2">
          <FaCheckCircle /> Calculated Quote
        </h4>

        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Truck</span>
            <span className="font-medium text-navy">{result.truck.label}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Destination</span>
            <span className="font-medium text-navy">{result.town.name} ({Math.round(result.town.km_from_edenvale)}km)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Fuel index</span>
            <span className="font-medium text-navy">Diesel R{result.fuel.current_per_l}/L</span>
          </div>
        </div>

        <div className="mt-3 border-t border-yellow-200 pt-2 space-y-1">
          {result.line_items.map((li, i) => {
            // Show the ACTUAL applied amount: base lines use the fuel-indexed
            // price (CPI + fuel applied); multi-qty add-ons show qty x rate.
            const qty = typeof li.quantity === 'number' && li.quantity ? li.quantity : 1;
            const displayAmt = (li.type === 'base' && typeof li.fuel_indexed_price === 'number')
              ? li.fuel_indexed_price
              : (qty > 1 ? (parseFloat(li.unit_price) || 0) * qty : (parseFloat(li.unit_price) || 0));
            return (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-gray-600 flex-1 pr-2">{li.description}</span>
                <span className="font-medium">{fmt(displayAmt)}</span>
              </div>
            );
          })}
          <div className="flex justify-between text-sm pt-1 border-t border-yellow-200">
            <span className="text-gray-600">Base cost</span>
            <span className="font-medium">{fmt(result.totals.base_cost)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Margin ({Math.round(result.margin_percentage * 100)}%)</span>
            <span className="font-medium">{fmt(result.totals.margin_amount)}</span>
          </div>
          <div className="flex justify-between text-base font-bold text-yellow-900 pt-1 border-t border-yellow-200">
            <span>Quote Total</span>
            <span>{fmt(result.totals.quote_total)}</span>
          </div>
          <div className="flex justify-between text-base font-bold text-navy">
            <span>Grand Total</span>
            <span>{fmt(result.totals.grand_total)}</span>
          </div>
        </div>

        {result.flags && result.flags.length > 0 && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2 space-y-1">
            {result.flags.map((f, i) => (
              <p key={i} className="text-xs text-amber-800 flex items-start gap-1">
                <FaExclamationTriangle className="mt-0.5" /> {f}
              </p>
            ))}
          </div>
        )}

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center gap-1"
          >
            <FaSyncAlt /> Re-run
          </button>
        </div>
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  const StepIcon = STEPS[step].icon;

  return (
    <div className="space-y-5">
      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          const done = i < step || (i === 3 && result);
          return (
            <React.Fragment key={s.id}>
              {i > 0 && <div className={`h-px flex-1 ${i <= step ? 'bg-yellow-400' : 'bg-gray-200'}`} />}
              <button
                type="button"
                onClick={() => i < step && setStep(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  active ? 'bg-yellow-400 text-navy border-yellow-400 shadow'
                    : done ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                    : 'bg-gray-50 text-gray-500 border-gray-200'
                }`}
              >
                <Icon size={11} /> {s.label}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Step content */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-base font-semibold text-navy mb-4 flex items-center gap-2">
          <StepIcon className="text-yellow-500" /> {STEPS[step].label}
        </h3>

        {step === 0 && renderFreight()}
        {step === 1 && renderSite()}
        {step === 2 && renderRoute()}
        {step === 3 && renderExtras()}

        {/* Nav */}
        <div className="mt-5 flex justify-between items-center">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 text-gray-600"
          >
            ← Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              className="px-5 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-yellow-400 hover:text-navy transition-colors flex items-center gap-1"
            >
              Next <FaArrowRight size={11} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCalculate}
              disabled={calculating}
              className="px-5 py-2 bg-yellow-400 text-navy rounded-lg text-sm font-bold hover:bg-yellow-300 transition-colors disabled:opacity-50 flex items-center gap-2 shadow"
            >
              {calculating ? <><FaSpinner className="animate-spin" /> Calculating…</> : <><FaCalculator /> Calculate Quote</>}
            </button>
          )}
        </div>
      </div>

      {/* Result */}
      {renderResult()}

      {/* Truck loader visual */}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <FaTruckLoading className="text-yellow-500" />
        <span>
          {result
            ? `${result.truck.label} · ${result.town.name} · diesel R${result.fuel.current_per_l}/L`
            : 'Answer the questions — the rate engine does the rest.'}
        </span>
      </div>
    </div>
  );
}

export default ContainerQuoteWizard;
